import { createSimpleAsyncAction } from 'async-utils';
import { snapshot } from 'valtio';
import {
  FilterElements,
  FilterTypeSearch,
} from '../../common/filters-utils/filters-utils';
import auditLogsService from '../services';
import {
  addAuditLogs,
  clearAuditLogs,
  clearSelectedLog,
  getAuditLogsQueryInput,
  setAuditLogFilters,
  setSearchValues,
  setStoreAuditLogFiltersTypeSearch,
  setTotal,
} from '../store-actions/audit-logs-store-actions';
import AuditLogsStore from '../stores/audit-logs-store';
import { AuditLogsError } from '../types/audit-logs-types';

export const loadAuditLogs = createSimpleAsyncAction(
  async (params?: { includeCount: boolean }) => {
    let { includeCount = false } = params || {};
    const { offset, total } = snapshot(AuditLogsStore);

    if (total && total === offset) {
      return;
    }

    if (offset > total) {
      // this should prevent a case where on scroll races with load and we have wrong numbers on total and offset, so we clear
      includeCount = true;
      clearAuditLogs();
    }

    const input = getAuditLogsQueryInput();

    const auditLogsResponse = await auditLogsService.getAuditLogs.execute(
      input,
      includeCount,
    );

    if (!auditLogsResponse) {
      return;
    }

    const { auditLogs, logsCount } = auditLogsResponse;
    addAuditLogs(auditLogs);
    setTotal(logsCount);
  },
  {
    asyncState: AuditLogsStore.loading,
    errorMessage: AuditLogsError.FailedLoadAuditLogs,
  },
);

export const loadAuditLogFilters = createSimpleAsyncAction(
  async () => {
    const input = getAuditLogsQueryInput();

    const filters = await auditLogsService.getAuditLogFilters.execute(input);
    if (!filters) {
      return;
    }

    setStoreAuditLogFiltersTypeSearch(filters);
    setAuditLogFilters(filters);
  },
  {
    asyncState: AuditLogsStore.loadingFilters,
    errorMessage: AuditLogsError.FailedLoadAuditLogs,
  },
);

export const loadLogsAndFilters = async () => {
  clearAuditLogs();
  clearSelectedLog();

  const loadAuditLogsPromise = loadAuditLogs({ includeCount: true });
  const loadAuditLogFiltersPromise = loadAuditLogFilters();
  await Promise.all([loadAuditLogsPromise, loadAuditLogFiltersPromise]);
};

export const clearFilters = () => {
  AuditLogsStore.filterBy = {};
  AuditLogsStore.numberOfFilters = 0;

  loadLogsAndFilters();
};

export const clearFilter = (type: string) => {
  AuditLogsStore.filterBy[type] = [];

  loadLogsAndFilters();
};

export const onChangeFilter = (e: React.ChangeEvent<HTMLInputElement>) => {
  if (AuditLogsStore.filterBy[e.target.id]) {
    const index = AuditLogsStore.filterBy[e.target.id].indexOf(e.target.value);
    if (index !== -1) {
      AuditLogsStore.filterBy[e.target.id].splice(index, 1);
    } else {
      AuditLogsStore.filterBy[e.target.id].push(e.target.value);
    }
  } else {
    AuditLogsStore.filterBy[e.target.id] = [e.target.value];
  }

  loadLogsAndFilters();
};

export const handleSearchFilterType = (
  searchInput: string,
  filterType: string,
) => {
  const { filters } = snapshot(AuditLogsStore);
  if (filters && filterType) {
    const newFilteredTypes = filters[filterType].filter(
      (fType: FilterElements) =>
        fType.label.toLocaleLowerCase().includes(searchInput.toLowerCase()),
    );
    setSearchValues(filterType, searchInput);
    setStoreAuditLogFiltersTypeSearch({
      ...filters,
      [FilterTypeSearch[filterType]]: newFilteredTypes,
    });
  }
};
